import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_BASE_URL } from "../../config";
import ImageUpload from "../../Components/ImageUpload";

const EMPTY_FORM = {
  name: "",
  origin: "",
  description: "",
  image: "",
  variants: [{ weight: "", price: "" }],
  available: true,
};

// Mango used to be a hardcoded array duplicated across the client and
// server - no admin page, no database, so changing a price meant editing
// code and redeploying both apps. This is the real management UI for it.
const Mango = () => {
  const [varieties, setVarieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const token = localStorage.getItem("authToken");

  const fetchVarieties = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/mango`);
      setVarieties(response.data.varieties);
    } catch (err) {
      console.error("Error fetching mango varieties:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVarieties();
  }, []);

  const startCreate = () => {
    setError(null);
    setEditingId(null);
    setFormData(EMPTY_FORM);
  };

  const startEdit = (variety) => {
    setError(null);
    setEditingId(variety._id);
    setFormData({
      name: variety.name,
      origin: variety.origin || "",
      description: variety.description || "",
      image: variety.image,
      variants: variety.variants.map((v) => ({ weight: v.weight, price: String(v.price) })),
      available: variety.available,
    });
  };

  const updateVariant = (index, field, value) => {
    const variants = formData.variants.map((v, i) =>
      i === index ? { ...v, [field]: value } : v
    );
    setFormData({ ...formData, variants });
  };

  const addVariantRow = () => {
    setFormData({ ...formData, variants: [...formData.variants, { weight: "", price: "" }] });
  };

  const removeVariantRow = (index) => {
    setFormData({ ...formData, variants: formData.variants.filter((_, i) => i !== index) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!formData.name.trim() || !formData.image) {
      setError("Name and image are required.");
      return;
    }
    const cleanVariants = formData.variants
      .filter((v) => v.weight.trim() && v.price !== "")
      .map((v) => ({ weight: v.weight.trim(), price: Number(v.price) }));
    if (cleanVariants.length === 0) {
      setError("Add at least one weight/price variant.");
      return;
    }
    if (cleanVariants.some((v) => Number.isNaN(v.price) || v.price <= 0)) {
      setError("Every variant needs a positive price.");
      return;
    }

    const payload = { ...formData, variants: cleanVariants };

    setSubmitting(true);
    try {
      if (editingId) {
        await axios.put(`${API_BASE_URL}/mango/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        await axios.post(`${API_BASE_URL}/mango`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      startCreate();
      await fetchVarieties();
    } catch (err) {
      console.error("Error saving mango variety:", err);
      setError(err.response?.data?.message || "Failed to save.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this mango variety? This can't be undone.")) return;
    try {
      await axios.delete(`${API_BASE_URL}/mango/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setVarieties(varieties.filter((v) => v._id !== id));
      if (editingId === id) startCreate();
    } catch (err) {
      console.error("Error deleting mango variety:", err);
      alert(err.response?.data?.message || "Failed to delete.");
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-sub">Loading mango varieties…</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-4 text-ink">Mango Varieties</h2>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          {varieties.length === 0 ? (
            <p className="text-sub">No mango varieties yet - add the first one.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {varieties.map((variety) => (
                <div key={variety._id} className="bg-card border border-line rounded-lg p-4">
                  <img
                    src={variety.image}
                    alt={variety.name}
                    className="w-full h-32 object-cover rounded mb-2"
                  />
                  <h3 className="text-lg font-semibold text-ink">{variety.name}</h3>
                  {variety.origin && <p className="text-xs text-sub">{variety.origin}</p>}
                  <ul className="text-sm text-sub mt-1">
                    {variety.variants.map((v, i) => (
                      <li key={i}>
                        {v.weight}: ৳{v.price}
                      </li>
                    ))}
                  </ul>
                  {!variety.available && (
                    <span className="inline-block mt-1 text-xs px-2 py-0.5 rounded-full bg-red-50 text-red-700">
                      Unavailable
                    </span>
                  )}
                  <div className="flex justify-between mt-4">
                    <button
                      onClick={() => startEdit(variety)}
                      className="bg-primary text-white py-2 px-4 rounded hover:opacity-90 transition"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(variety._id)}
                      className="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 transition"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-card border border-line rounded-lg p-6 space-y-4 h-fit"
        >
          <h3 className="text-lg font-semibold text-ink">
            {editingId ? "Edit variety" : "Add a variety"}
          </h3>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </p>
          )}

          <input
            placeholder="Name (e.g. Amrapali)"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="w-full p-2 border border-line rounded"
          />
          <input
            placeholder="Origin (e.g. Naogaon)"
            value={formData.origin}
            onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
            className="w-full p-2 border border-line rounded"
          />
          <input
            placeholder="Short description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full p-2 border border-line rounded"
          />

          <ImageUpload
            label="Image"
            value={formData.image}
            onChange={(image) => setFormData({ ...formData, image })}
          />

          <div>
            <label className="block font-semibold mb-1 text-ink">Weight / Price variants</label>
            <div className="space-y-2">
              {formData.variants.map((variant, i) => (
                <div key={i} className="flex gap-2">
                  <input
                    placeholder="Weight (e.g. 12kg)"
                    value={variant.weight}
                    onChange={(e) => updateVariant(i, "weight", e.target.value)}
                    className="flex-1 p-2 border border-line rounded"
                  />
                  <input
                    type="number"
                    min="0"
                    step="1"
                    placeholder="Price"
                    value={variant.price}
                    onChange={(e) => updateVariant(i, "price", e.target.value)}
                    className="w-24 p-2 border border-line rounded"
                  />
                  <button
                    type="button"
                    onClick={() => removeVariantRow(i)}
                    className="text-red-600 px-2"
                    aria-label="Remove variant"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addVariantRow}
              className="mt-2 text-sm text-primary font-semibold"
            >
              + Add variant
            </button>
          </div>

          <label className="flex items-center text-ink">
            <input
              type="checkbox"
              checked={formData.available}
              onChange={(e) => setFormData({ ...formData, available: e.target.checked })}
              className="mr-2"
            />
            In season / available
          </label>

          <button
            type="submit"
            disabled={submitting}
            className="w-full bg-primary text-white p-3 rounded-lg font-bold hover:opacity-90 transition disabled:opacity-60"
          >
            {submitting ? "Saving…" : editingId ? "Save Changes" : "Add Variety"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={startCreate}
              className="w-full border border-line text-ink py-2 rounded hover:bg-paper transition"
            >
              Cancel Edit
            </button>
          )}
        </form>
      </div>
    </div>
  );
};

export default Mango;
